import classnames from 'classnames';
import React from 'react';

import { CarretLeftIcon, CarretRightIcon } from '../icons/SvgIcons';

type PaginationControlsProps = {
  activePage: number;
  totalPages: number;
  setActivePage: (page: number) => void;
};
const PaginationControls: React.FC<PaginationControlsProps> = React.memo(({ activePage, setActivePage, totalPages }) => {
  return (
    <nav aria-label="Pagination Controls" className="flex justify-start my-2">
      <ol className="flex justify-center text-lg justify-content-start gap-2">
        <NavigationButton isDisabled={activePage === 0} onClick={() => setActivePage(Math.max(activePage - 1, 0))}>
          <CarretLeftIcon />
        </NavigationButton>
        <div className="flex gap-2 justify-center items-center">
          {new Array(totalPages).fill(true).map((_, pageIndex) => (
            <PageButton key={`page-control-${pageIndex}`} isActive={activePage === pageIndex} onClick={() => setActivePage(pageIndex)}>
              {pageIndex + 1}
            </PageButton>
          ))}
        </div>
        <NavigationButton isDisabled={activePage === totalPages - 1} onClick={() => setActivePage(Math.min(activePage + 1, totalPages))}>
          <CarretRightIcon />
        </NavigationButton>
      </ol>
    </nav>
  );
});
PaginationControls.displayName = 'PaginationControls';
export default PaginationControls;

type PageButtonProps = {
  children: React.ReactNode;
  isActive?: boolean;
  onClick: () => void;
};
const PageButton: React.FC<PageButtonProps> = React.memo(({ children, isActive, onClick }) => {
  return (
    <li>
      <button
        aria-label="Navigate to page"
        onClick={onClick}
        className={classnames('select-none block h-12 w-12 text-center border-1 rounded-full', {
          'border-darkGray': !isActive,
          'border-blueElectric': isActive,
          'bg-blueElectric': isActive,
        })}
      >
        {children}
      </button>
    </li>
  );
});
PageButton.displayName = 'PageButton';

type NavigationButtonProps = {
  children: React.ReactNode;
  isActive?: boolean;
  isDisabled?: boolean;
  onClick: () => void;
};
const NavigationButton: React.FC<NavigationButtonProps> = React.memo((props) => {
  const { children, isDisabled, onClick } = props;
  return (
    <li>
      <button
        aria-label="Navigation button"
        disabled={isDisabled}
        aria-disabled={isDisabled}
        className={`select-none h-12 w-12 border-1 rounded-full border-darkGray flex items-center justify-center`}
        onClick={onClick}
      >
        {children}
      </button>
    </li>
  );
});
NavigationButton.displayName = 'NavigationButton';
